import React, { ReactNode, forwardRef } from 'react';
import { genericMemo } from '@direct-frontend/stdlib';
import { getPlatformStyles } from '@direct-frontend/platform-code';
import { mergeProps, useButton, useFocusRing } from 'react-aria';

import { HVStackHookOptions, useVStack } from '../../hooks/useStack';
import { cn } from '../../lib/css/className/className';
import { CardPaddingCorrection } from '../Card/Card';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { getDataAttributesProps } from '../../lib/react/getDataAttributesProps/getDataAttributesProps';
import { useObjectRef } from '../../hooks/useObjectRef';
import { type DataAttributesProps } from '../../internal/types/dataAttributesTypes';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { LayoutComponentsLayoutProps, useLayoutStyle } from '../../lib/layoutStyle/layoutStyle';

getPlatformStyles({
    common: import('./Island.css'),
    mobile: import('./Island@mobile.css'),
    desktop: import('./Island@desktop.css'),
});

const cls = cn('Island');

export interface IslandProps extends Omit<HVStackHookOptions, 'ref'>, LayoutComponentsLayoutProps, DataAttributesProps {
    /**
     * Дочерние элементы
     */
    children: ReactNode;

    /**
     * Корректировка отступов сверху и снизу.
     * Используется, когда требуется сделать верхний либо верхний и нижний отступ меньше, чем по бокам.
     * @default top
     */
    paddingCorrection?: CardPaddingCorrection;

    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Атрибут id для элемента
     */
    id?: string;

    /**
     * Обработчик нажатия на элемент
     */
    onClick?: () => void;

    /**
     * Корректировка скругления верхних углов
     * @default false
     */
    topBorderRadiusCorrection?: boolean;

    /**
     * Корректировка скругления нижних углов
     * @default false
     */
    bottomBorderRadiusCorrection?: boolean;

    /**
     * Флаг наличия ошибки в острове (используется для подскролла к заданному острову)
     */
    'data-form-error'?: boolean;

    /**
     * Признак отключения взаимодействия с Island
     */
    isDisabled?: boolean;
}

/**
 * Компонент для создания логически независимых блоков
 */
export const Island = genericMemo(
    forwardRef<HTMLDivElement, IslandProps>(function Island(
        {
            className,
            children,
            paddingCorrection = 'top',
            topBorderRadiusCorrection = false,
            bottomBorderRadiusCorrection = false,
            id,
            isDisabled,
            onClick,
            'data-testid': dataTestId,
            'data-form-error': dataFormError,

            width,
            minWidth,
            maxWidth,
            height,
            minHeight,
            maxHeight,
            flex,

            ...props
        },
        forwardedRef,
    ) {
        const ref = useObjectRef(forwardedRef);
        const { buttonProps } = useButton({ onPress: onClick, elementType: 'div', isDisabled }, ref);
        const { focusProps, isFocusVisible } = useFocusRing();
        const isInteractive = Boolean(onClick) && !isDisabled;

        const skinClass = getColorSkin('gray-neutral', {});
        const skinSizeClass = getSizeSkin('layout');
        const stackClasses = useVStack(props);

        const { style: layoutStyle, className: layoutClassName } = useLayoutStyle({
            width,
            minWidth,
            maxWidth,
            height,
            minHeight,
            maxHeight,
            flex,
        });

        return (
            <div
                {...(onClick && mergeProps(buttonProps, focusProps))}
                {...getDataAttributesProps(props)}
                ref={ref}
                className={cls(
                    {
                        paddingCorrection,
                        topBorderRadiusCorrection,
                        bottomBorderRadiusCorrection,
                        focused: isFocusVisible,
                        interactive: isInteractive,
                    },
                    [className, stackClasses, skinSizeClass, skinClass, layoutClassName],
                )}
                style={layoutStyle}
                data-testid={dataTestId}
                data-form-error={dataFormError}
                id={id}
            >
                {children}
            </div>
        );
    }),
);
